import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ToegangSsoConfig } from '../../environments/environment.type';
import { publicApi } from '../http/http-context-tokens';

/** Result of one silent-SSO attempt. The shape is exhaustive so callers can
 *  switch on `status` without falling through. */
export type ToegangSsoResult =
  | { status: 'ok'; token: string }
  | { status: 'no-session' }
  | { status: 'disabled' }
  | { status: 'timeout' }
  | { status: 'error' };

// Tight enough that a blocked iframe (X-Frame-Options, third-party-cookie
// blocking, blocked frame-ancestors CSP) doesn't visibly stall app boot,
// loose enough that a real round-trip to Toegang's IdP fits comfortably.
const TIMEOUT_MS = 1500;

// Claims requested in the id_token, matching what the backend reads on the
// /api/auth/toegang-sso exchange.
const REQUESTED_CLAIMS = JSON.stringify({
  id_token: {
    bp: null,
    licenses: null,
    email: null,
    first_name: null,
    tussenvoegsels: null,
    family_name: null,
  },
});

/**
 * Drives the OIDC `prompt=none` silent-SSO bootstrap on top of
 * `angular-oauth2-oidc`. The library owns the hidden-iframe lifecycle, the
 * nonce/state generation and their validation on the way back; this service
 * only configures it, kicks off `silentRefresh()`, and exchanges the resulting
 * Toegang id_token at the backend for an NT2 session JWT.
 *
 * The NT2 JWT (not Toegang's id_token) is onegate's session of record — it's
 * stored as a cookie by AuthService — so the id_token is dropped from the
 * library's storage immediately after the exchange.
 *
 * Single-flight: concurrent calls share the same in-flight promise so
 * APP_INITIALIZER and any later opportunistic re-check never run twice.
 */
@Service()
export class ToegangSsoService {
  readonly #http = inject(HttpClient);

  readonly #oauth = inject(OAuthService);

  #configured = false;

  #inflight: Promise<ToegangSsoResult> | null = null;

  checkSession(): Promise<ToegangSsoResult> {
    if (this.#inflight) {
      return this.#inflight;
    }
    // Dev-only short-circuit. When set on environment.local.ts the iframe
    // round-trip is skipped — handy on hosts where Toegang doesn't whitelist
    // the silent-callback redirect_uri. Ignored in production builds so an
    // accidentally-committed stub in environment.prod.ts can't bypass the
    // real probe (the backend stays the source of truth either way — a
    // forged session token won't survive the next API call).
    const stub = environment.toegangSsoStub;
    if (stub && !environment.production) {
      return Promise.resolve(stub);
    }
    const config = environment.toegangSso;
    if (!config) {
      return Promise.resolve({ status: 'disabled' });
    }

    this.#inflight = this.#run(config).finally(() => {
      this.#inflight = null;
    });
    return this.#inflight;
  }

  async #run(config: ToegangSsoConfig): Promise<ToegangSsoResult> {
    this.#configure(config);

    try {
      // A resolved promise means a fresh id_token landed in the library's
      // storage (nonce + state already validated). A rejection is either a
      // timeout or Toegang declining the silent prompt (login_required).
      await this.#oauth.silentRefresh();
    } catch (error: unknown) {
      return error instanceof OAuthErrorEvent &&
        error.type === 'silent_refresh_timeout'
        ? { status: 'timeout' }
        : { status: 'no-session' };
    }

    const idToken = this.#oauth.getIdToken();
    // Drop Toegang's token from the library's storage; the NT2 session JWT the
    // backend returns is our session of record. logOut(true) clears storage
    // without redirecting to an end-session endpoint.
    this.#oauth.logOut(true);

    if (!idToken) {
      return { status: 'no-session' };
    }

    try {
      const response = await firstValueFrom(
        this.#http.post<{ session: string }>(
          '/api/auth/toegang-sso',
          { id_token: idToken },
          publicApi(),
        ),
      );
      return { status: 'ok', token: response.session };
    } catch {
      return { status: 'error' };
    }
  }

  #configure(config: ToegangSsoConfig): void {
    if (this.#configured) {
      return;
    }

    const authConfig: AuthConfig = {
      loginUrl: config.authUrl,
      clientId: config.clientId,
      redirectUri: config.silentRedirectUri,
      silentRefreshRedirectUri: config.silentRedirectUri,
      responseType: 'id_token token',
      scope: 'openid',
      oidc: true,
      requestAccessToken: true,
      // The backend (JwksToegangIdTokenVerifier) is the source of truth for the
      // id_token, so the frontend registers no ValidationHandler and skips the
      // signature, at_hash and issuer checks. The library still generates and
      // validates the nonce + state for this silent flow.
      skipIssuerCheck: true,
      disableAtHashCheck: true,
      silentRefreshTimeout: TIMEOUT_MS,
      customQueryParams: { claims: REQUESTED_CLAIMS },
    };

    this.#oauth.configure(authConfig);
    this.#configured = true;
  }
}
